import { useEffect, useRef, useState } from 'react';
import { isTouchViewport } from '@/hooks/use-mobile';

interface UseDraggableOptions {
	defaultPosition?: { rightPercent: number; topPercent: number };
	bounds?: { minPercent: number; maxPercent: number };
}

interface UseDraggableReturn {
	position: { rightPercent: number; topPercent: number };
	startDrag: (e: React.MouseEvent | React.TouchEvent) => void;
	isDragging: boolean;
}

const clampPercent = (value: number, max: number): number =>
	Math.min(Math.max(value, 0), max);

type Position = { rightPercent: number; topPercent: number };
type Size = { width: number; height: number };

// On touch screens (tablets) a card dragged past the edge can't be grabbed
// again, so keep all of it on screen. Desktop keeps the percentage bounds.
const keepOnScreen = (next: Position, size: Size | null): Position => {
	if (!size || !isTouchViewport()) return next;
	const maxRight = Math.max(0, window.innerWidth - size.width);
	const maxTop = Math.max(0, window.innerHeight - size.height);
	const right = Math.min(Math.max((next.rightPercent / 100) * window.innerWidth, 0), maxRight);
	const top = Math.min(Math.max((next.topPercent / 100) * window.innerHeight, 0), maxTop);
	return {
		rightPercent: (right / window.innerWidth) * 100,
		topPercent: (top / window.innerHeight) * 100,
	};
};

export const useDraggable = (
	options: UseDraggableOptions = {}
): UseDraggableReturn => {
	const {
		defaultPosition = { rightPercent: 1.5, topPercent: 1.5 },
		bounds = { minPercent: 0, maxPercent: 98 },
	} = options;

	const [position, setPosition] = useState<{
		rightPercent: number;
		topPercent: number;
	}>(defaultPosition);
	const [isDragging, setIsDragging] = useState(false);

	const dragStateRef = useRef<{
		dragging: boolean;
		startX: number;
		startY: number;
		startRight: number;
		startTop: number;
	}>({
		dragging: false,
		startX: 0,
		startY: 0,
		startRight: 0,
		startTop: 0,
	});

	// The card's size at the last drag, for keeping it on screen.
	const sizeRef = useRef<Size | null>(null);

	const savePosition = (next: {
		rightPercent: number;
		topPercent: number;
	}): void => {
		setPosition(keepOnScreen(next, sizeRef.current));
	};

	// Rotating a tablet can leave a dragged card off screen; pull it back.
	useEffect(() => {
		const handleResize = (): void => {
			if (!sizeRef.current) return;
			setPosition((current) => keepOnScreen(current, sizeRef.current));
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Handle drag events
	useEffect(() => {
		const handlePointerMove = (event: MouseEvent | TouchEvent): void => {
			if (!dragStateRef.current.dragging) return;
			event.preventDefault();
			const point =
				event instanceof TouchEvent
					? event.touches[0]
					: (event as MouseEvent);
			const deltaX = point.clientX - dragStateRef.current.startX;
			const deltaY = point.clientY - dragStateRef.current.startY;
			const newRight = dragStateRef.current.startRight - deltaX;
			const newTop = dragStateRef.current.startTop + deltaY;
			const newRightPercent = (newRight / window.innerWidth) * 100;
			const newTopPercent = (newTop / window.innerHeight) * 100;
			savePosition({
				rightPercent: clampPercent(newRightPercent, bounds.maxPercent),
				topPercent: clampPercent(newTopPercent, bounds.maxPercent),
			});
		};

		const handlePointerUp = (): void => {
			if (dragStateRef.current.dragging) {
				dragStateRef.current.dragging = false;
				setIsDragging(false);
			}
		};

		window.addEventListener('mousemove', handlePointerMove);
		window.addEventListener('mouseup', handlePointerUp);
		window.addEventListener('touchmove', handlePointerMove, {
			passive: false,
		});
		window.addEventListener('touchend', handlePointerUp);

		return () => {
			window.removeEventListener('mousemove', handlePointerMove);
			window.removeEventListener('mouseup', handlePointerUp);
			window.removeEventListener('touchmove', handlePointerMove);
			window.removeEventListener('touchend', handlePointerUp);
		};
	}, [bounds.maxPercent]);

	const startDrag = (event: React.MouseEvent | React.TouchEvent): void => {
		const target = event.target as HTMLElement;
		if (
			target.closest('button') ||
			target.closest('input') ||
			target.closest('textarea') ||
			target.closest('select')
		) {
			return;
		}
		if ('button' in event && event.button !== 0) return;

		const point =
			'touches' in event
				? (event as React.TouchEvent).touches[0]
				: (event as React.MouseEvent);
		dragStateRef.current.dragging = true;
		setIsDragging(true);
		dragStateRef.current.startX = point.clientX;
		dragStateRef.current.startY = point.clientY;
		const container = (event.currentTarget as HTMLElement).parentElement;
		if (container) {
			const rect = container.getBoundingClientRect();
			dragStateRef.current.startRight = window.innerWidth - rect.right;
			dragStateRef.current.startTop = rect.top;
			sizeRef.current = { width: rect.width, height: rect.height };
		}
	};

	return {
		position,
		startDrag,
		isDragging,
	};
};
